import { useCallback, useEffect, useState } from 'react'

interface UseTabsOptions<TTabKey extends string> {
  tabs: readonly TTabKey[]
  defaultTab: TTabKey
  canLeave?: (current: TTabKey, next: TTabKey) => boolean | Promise<boolean>
  isEnabled?: (tab: TTabKey) => boolean
  isReady?: boolean
}

function useTabs<TTabKey extends string>(options: UseTabsOptions<TTabKey>) {
  const {
    tabs, defaultTab, isEnabled, canLeave, isReady = true,
  } = options

  const [activeTab, setActiveTab] = useState<TTabKey>(defaultTab)

  useEffect(() => {
    if (!isReady) return

    const hash = window.location.hash.replace('#', '') as TTabKey
    if (tabs.includes(hash) && (!isEnabled || isEnabled(hash))) {
      setActiveTab(hash)
    } else {
      setActiveTab(defaultTab)
      window.history.pushState('', document.title, window.location.href.split('#')[0])
    }
  }, [isReady, defaultTab, isEnabled, tabs])

  const goToTab = useCallback(
    async (next: TTabKey) => {
      if (next === activeTab) return

      if (isEnabled && !isEnabled(next)) {
        return
      }

      if (canLeave) {
        const allowed = await canLeave(activeTab, next)
        if (!allowed) return
      }

      setActiveTab(next)
      window.history.replaceState(null, '', `#${next}`)
    },
    [activeTab, canLeave, isEnabled],
  )

  return {
    activeTab,
    goToTab,
  }
}

export default useTabs
